// Generated by ReScript, PLEASE EDIT WITH CARE

import * as Emotion from "../../bindings/Emotion.bs.js";
import * as JsxRuntime from "react/jsx-runtime";

let baseStyles = Emotion.Css.css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: Emotion.Utils.Border.getRadius("md"),
  border: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  fontWeight: "500",
  textDecoration: "none",
  transition: Emotion.Utils.Animate.getDuration("fast") + " ease-out",
  userSelect: "none",
  whiteSpace: "nowrap",
  position: "relative",
  overflow: "hidden",
  outline: "none"
});

function getSizeStyles(size) {
  if (size === "md") {
    return Emotion.Css.css({
      height: "2.5rem",
      padding: "0.625rem 1rem",
      fontSize: "1rem"
    });
  } else if (size === "sm") {
    return Emotion.Css.css({
      height: "2rem",
      padding: "0.5rem 0.75rem",
      fontSize: "0.875rem"
    });
  } else if (size === "xl") {
    return Emotion.Css.css({
      height: "3.5rem",
      padding: "0.875rem 1.5rem",
      fontSize: "1.25rem"
    });
  } else if (size === "xs") {
    return Emotion.Css.css({
      height: "2rem",
      padding: "0 0.75rem",
      fontSize: "0.875rem"
    });
  } else {
    return Emotion.Css.css({
      height: "3rem",
      padding: "0.75rem 1.25rem",
      fontSize: "1.125rem"
    });
  }
}

function getVariantStyles(variant) {
  if (variant === "secondary") {
    return Emotion.Css.css({
      backgroundColor: Emotion.Utils.Color.border,
      color: Emotion.Utils.Color.textPrimary,
      ":hover": {
        backgroundColor: Emotion.Utils.Color.borderHover
      },
      ":focus": {
        boxShadow: Emotion.Utils.Shadow.getFocusShadow("primary")
      }
    });
  } else if (variant === "primary") {
    return Emotion.Css.css({
      backgroundColor: Emotion.Utils.Color.primary,
      color: Emotion.Utils.Color.textInverse,
      ":hover": {
        backgroundColor: Emotion.Utils.Color.primaryDark
      },
      ":focus": {
        boxShadow: Emotion.Utils.Shadow.getFocusShadow("primary")
      },
      ":active": {
        backgroundColor: Emotion.Utils.Color.primaryDark,
        transform: "translateY(1px)"
      }
    });
  } else if (variant === "ghost") {
    return Emotion.Css.css({
      backgroundColor: "transparent",
      color: Emotion.Utils.Color.primary,
      ":hover": {
        backgroundColor: Emotion.Utils.Color.primary + `14`
      },
      ":focus": {
        boxShadow: Emotion.Utils.Shadow.getFocusShadow("primary")
      }
    });
  } else if (variant === "danger") {
    return Emotion.Css.css({
      backgroundColor: Emotion.Utils.Color.error,
      color: "white",
      ":hover": {
        backgroundColor: Emotion.Utils.Color.errorDark
      },
      ":focus": {
        boxShadow: Emotion.Utils.Shadow.getFocusShadow("error")
      }
    });
  } else {
    return Emotion.Css.css({
      backgroundColor: "transparent",
      color: Emotion.Utils.Color.primary,
      border: `1px solid ` + Emotion.Utils.Color.primary,
      ":hover": {
        backgroundColor: Emotion.Utils.Color.primary,
        color: Emotion.Utils.Color.textInverse
      },
      ":focus": {
        boxShadow: Emotion.Utils.Shadow.getFocusShadow("primary")
      }
    });
  }
}

function getDisabledStyles() {
  return Emotion.Css.css({
    opacity: "0.5",
    cursor: "not-allowed",
    pointerEvents: "none"
  });
}

function getLoadingStyles() {
  return Emotion.Css.css({
    color: "transparent",
    position: "relative",
    pointerEvents: "none"
  });
}

function getFullWidthStyles() {
  return Emotion.Css.css({
    width: "100%"
  });
}

function Button$Button(props) {
  let __type_ = props.type_;
  let __className = props.className;
  let rightIcon = props.rightIcon;
  let leftIcon = props.leftIcon;
  let __fullWidth = props.fullWidth;
  let __loading = props.loading;
  let __disabled = props.disabled;
  let __size = props.size;
  let __variant = props.variant;
  let variant = __variant !== undefined ? __variant : "primary";
  let size = __size !== undefined ? __size : "md";
  let disabled = __disabled !== undefined ? __disabled : false;
  let loading = __loading !== undefined ? __loading : false;
  let fullWidth = __fullWidth !== undefined ? __fullWidth : false;
  let className = __className !== undefined ? __className : "";
  let type_ = __type_ !== undefined ? __type_ : "button";
  let buttonClassName = Emotion.Css.cx([
    baseStyles,
    getSizeStyles(size),
    getVariantStyles(variant),
    disabled ? getDisabledStyles() : "",
    loading ? getLoadingStyles() : "",
    fullWidth ? getFullWidthStyles() : "",
    className
  ]);
  let typeStr = type_ === "reset" ? "reset" : (
      type_ === "submit" ? "submit" : "button"
    );
  return JsxRuntime.jsxs("button", {
    children: [
      leftIcon !== undefined ? JsxRuntime.jsx("span", {
          children: leftIcon,
          className: Emotion.Css.css({
            marginRight: Emotion.Utils.Space.getSpacing("2")
          })
        }) : null,
      loading ? JsxRuntime.jsx("span", {
          children: JsxRuntime.jsx("span", {
            className: Emotion.Css.css({
              display: "inline-block",
              width: "1rem",
              height: "1rem",
              border: "2px solid transparent",
              borderTop: "2px solid currentColor",
              borderRadius: "50%",
              animation: "spin 1s linear infinite"
            })
          }),
          className: Emotion.Css.css({
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)"
          })
        }) : null,
      JsxRuntime.jsx("span", {
        children: props.children,
        className: loading ? Emotion.Css.css({
            opacity: "0"
          }) : ""
      }),
      rightIcon !== undefined ? JsxRuntime.jsx("span", {
          children: rightIcon,
          className: Emotion.Css.css({
            marginLeft: Emotion.Utils.Space.getSpacing("2")
          })
        }) : null
    ],
    "aria-label": props.ariaLabel,
    className: buttonClassName,
    disabled: disabled || loading,
    type: typeStr,
    onClick: props.onClick
  });
}

let Button = {
  baseStyles: baseStyles,
  getSizeStyles: getSizeStyles,
  getVariantStyles: getVariantStyles,
  getDisabledStyles: getDisabledStyles,
  getLoadingStyles: getLoadingStyles,
  getFullWidthStyles: getFullWidthStyles,
  make: Button$Button
};

export {
  Button,
}
/* baseStyles Not a pure module */
